/**
 * Payout / profit calculator (#777). All maths comes from the order ticket's own functions: centsToPx + economics
 * (ticket/order.ts) and validatePrice / validateSize / minOrderValueError (trading/rules.ts). No formulas here.
 */
import { useId } from 'react';
import { formatUsd, pxToCents } from '../market/format';
import { impliedProbabilityPct } from '../trading/rules';
import { evaluateCalc, type CalcState } from './calc';
import { delocalizeInput, localizeNumber } from '../i18n/format';
import { useT } from '../i18n/useT';

const SIDE_KEYS = ['common.yes', 'common.no'] as const;

export default function PayoutCalculator({ state, onChange }: { state: CalcState; onChange: (s: CalcState) => void }) {
  const priceId = useId();
  const sharesId = useId();
  const errId = useId();
  const t = useT();
  const { px, errors, econ } = evaluateCalc(state);
  const sideName = t(SIDE_KEYS[state.side]);
  const pct = px ? impliedProbabilityPct(px) : null;

  return (
    <div className="space-y-3" data-testid="payout-calc">
      <div role="radiogroup" aria-label={t('learn.calc.side_group')} className="flex max-w-xs rounded-lg bg-surface-2/50 p-1">
        {([0, 1] as const).map((s) => (
          <button
            key={s}
            type="button"
            role="radio"
            aria-checked={state.side === s}
            onClick={() => onChange({ ...state, side: s })}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-semibold ${
              state.side === s ? (s === 0 ? 'bg-yes/20 text-yes' : 'bg-no/20 text-no') : 'text-muted hover:text-fg'
            }`}
          >
            {t('order.action_buy', { side: t(SIDE_KEYS[s]) })}
          </button>
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label htmlFor={priceId} className="block text-xs text-muted">
          {t('learn.calc.price')}
          <input
            id={priceId}
            inputMode="decimal"
            value={state.cents}
            onChange={(e) => onChange({ ...state, cents: delocalizeInput(e.target.value) })}
            aria-describedby={errors.length ? errId : undefined}
            aria-invalid={errors.length > 0}
            className="num mt-1 w-full rounded-md border border-border bg-bg px-3 py-2 text-base text-fg"
            data-testid="calc-price"
          />
        </label>
        <label htmlFor={sharesId} className="block text-xs text-muted">
          {t('common.shares')}
          <input
            id={sharesId}
            inputMode="numeric"
            value={state.shares}
            onChange={(e) => onChange({ ...state, shares: e.target.value })}
            aria-describedby={errors.length ? errId : undefined}
            aria-invalid={errors.length > 0}
            className="num mt-1 w-full rounded-md border border-border bg-bg px-3 py-2 text-base text-fg"
            data-testid="calc-shares"
          />
        </label>
      </div>

      <dl
        className="grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-md bg-surface-2/50 p-3 text-sm"
        aria-live="polite"
        data-testid="calc-results"
      >
        <dt className="text-muted">{t('learn.calc.cost')}</dt>
        <dd className="num text-end" data-testid="calc-cost">
          {econ ? formatUsd(econ.notional) : '—'}
        </dd>
        <dt className="text-muted">{t('learn.calc.max_payout', { side: sideName })}</dt>
        <dd className="num text-end" data-testid="calc-payout">
          {econ?.maxPayout ? formatUsd(econ.maxPayout) : '—'}
        </dd>
        <dt className="text-muted">{t('learn.calc.profit')}</dt>
        <dd className="num text-end text-yes" data-testid="calc-profit">
          {econ?.profit ? `+${formatUsd(econ.profit)}` : '—'}
        </dd>
        <dt className="text-muted">{t('learn.calc.loss')}</dt>
        <dd className="num text-end text-no" data-testid="calc-loss">
          {/* A losing share pays $0, so a buy loses exactly its cost (economics.notional). */}
          {econ ? `−${formatUsd(econ.notional)}` : '—'}
        </dd>
      </dl>
      {errors.length > 0 ? (
        <ul id={errId} className="space-y-0.5 text-xs text-danger" data-testid="calc-errors">
          {errors.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted">
          {t('learn.calc.summary', {
            shares: state.shares.trim(),
            side: sideName,
            px: px ? pxToCents(px) : '—',
            pct: pct === null ? '' : localizeNumber(pct),
          })}
        </p>
      )}
    </div>
  );
}
